import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ArrowUpRight,
  ArrowDownLeft,
  Clock,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { transactionService } from "../services/transactionService";
import { depositService } from "../services/depositService";
import { formatMoney } from "../utils/money";

const HistoryPage = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    wallet_type: "",
    direction: "",
    date_from: "",
    date_to: "",
  });

  const observerTarget = useRef(null);
  const offsetRef = useRef(0);

  const tabs = [
    { id: "all", label: "All" },
    { id: "transactions", label: "Purchases" },
    { id: "deposits", label: "Deposits" },
  ];

  const loadData = useCallback(
    async (isLoadMore = false) => {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        offsetRef.current = 0;
      }

      try {
        const limit = 15;
        const currentOffset = isLoadMore ? offsetRef.current : 0;

        let newData = [];
        let hasMoreData = false;

        if (activeTab === "transactions") {
          const response = await transactionService.getTransactionHistory({
            ...filters,
            limit,
            offset: currentOffset,
          });
          newData = (response.data.transactions || []).map((item) => ({
            ...item,
            type_display: "purchase",
            reference: item.transaction_ref,
          }));
          hasMoreData = response.data.pagination?.has_more || false;
        } else if (activeTab === "deposits") {
          const response = await depositService.getDepositHistory({
            ...filters,
            limit,
            offset: currentOffset,
          });
          newData = (response.data.deposits || []).map((item) => ({
            ...item,
            type_display: "deposit",
            reference: item.deposit_ref,
            direction: "credit",
          }));
          hasMoreData = response.data.pagination?.has_more || false;
        } else {
          const [transactionResponse, depositResponse] = await Promise.all([
            transactionService.getTransactionHistory({
              ...filters,
              limit: 8,
              offset: Math.floor(currentOffset / 2),
            }),
            depositService.getDepositHistory({
              ...filters,
              limit: 7,
              offset: Math.floor(currentOffset / 2),
            }),
          ]);

          const transactions = (
            transactionResponse.data.transactions || []
          ).map((item) => ({
            ...item,
            type_display: "purchase",
            reference: item.transaction_ref,
          }));

          const deposits = (depositResponse.data.deposits || []).map(
            (item) => ({
              ...item,
              type_display: "deposit",
              reference: item.deposit_ref,
              direction: "credit",
            }),
          );

          newData = [...transactions, ...deposits].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          );
          hasMoreData =
            transactionResponse.data.pagination?.has_more ||
            depositResponse.data.pagination?.has_more ||
            false;
        }

        if (isLoadMore) {
          setData((prev) => [...prev, ...newData]);
          offsetRef.current += limit;
        } else {
          setData(newData);
          offsetRef.current = limit;
        }

        setHasMore(hasMoreData);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [activeTab, filters],
  );

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadData(true);
        }
      },
      { threshold: 0.5 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, loadData]);

  const formatAmount = (amount) => {
    return formatMoney(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "Today";
    if (diffDays === 2) return "Yesterday";
    if (diffDays <= 7) return `${diffDays - 1} days ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "text-emerald-600 bg-emerald-50";
      case "pending":
        return "text-amber-600 bg-amber-50";
      case "processing":
        return "text-blue-600 bg-blue-50";
      case "failed":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getTransactionIcon = (item) => {
    if (item.type_display === "deposit" || item.direction === "credit") {
      return (
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
          <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
        </div>
      );
    }
    return (
      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
        <ArrowUpRight className="w-5 h-5 text-red-600" />
      </div>
    );
  };

  const getTitle = (item) => {
    if (item.type_display === "deposit") {
      return "Wallet Deposit";
    }
    if (item.description) {
      return item.description; // Removed truncation
    }
    return "Purchase Transaction";
  };

  const getWalletDisplayName = (walletType) => {
    switch (walletType) {
      case "affordable_data":
        return "Affordable Data";
      case "airtime_data":
        return "Airtime & Data";
      default:
        return walletType?.replace(/_/g, " ") || "";
    }
  };

  const filteredData = data.filter((item) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (item.reference || "").toLowerCase().includes(search) ||
      (item.description || "").toLowerCase().includes(search) ||
      (item.recipient_phone || "").toLowerCase().includes(search)
    );
  });

  const SkeletonLoader = () => (
    <div className="bg-white rounded-2xl p-4 mb-3 border border-gray-100">
      <div className="flex items-start space-x-3">
        <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded animate-pulse mb-3"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
        </div>
        <div className="text-right">
          <div className="h-5 bg-gray-200 rounded animate-pulse w-16 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-12"></div>
        </div>
      </div>
    </div>
  );

  const TransactionItem = ({ item }) => (
    <div className="bg-white rounded-2xl p-4 mb-3 border border-gray-100">
      <div className="flex items-start space-x-3">
        {getTransactionIcon(item)}

        <div className="flex-1 min-w-0">
          {/* Title and Amount Row */}
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 leading-5">
              {getTitle(item)}
            </h3>
            <div className="text-right ml-3 flex-shrink-0">
              <p
                className={`text-base sm:text-lg font-bold ${
                  item.type_display === "deposit" || item.direction === "credit"
                    ? "text-emerald-600"
                    : "text-red-600"
                }`}
              >
                {item.type_display === "deposit" || item.direction === "credit"
                  ? "+"
                  : "-"}
                ₵
                {formatAmount(
                  item.type_display === "deposit"
                    ? item.total_credited || item.amount
                    : item.amount,
                )}
              </p>
            </div>
          </div>

          {/* Details Section - Stacked on Mobile */}
          <div className="space-y-2">
            {/* Wallet Type */}
            {item.wallet_type && (
              <div className="flex items-center">
                <span className="text-xs text-gray-500 font-medium">
                  Wallet:
                </span>
                <span className="text-xs text-gray-700 ml-1">
                  {getWalletDisplayName(item.wallet_type)}
                </span>
              </div>
            )}

            {/* Recipient Phone */}
            {item.recipient_phone && (
              <div className="flex items-center">
                <span className="text-xs text-gray-500 font-medium">To:</span>
                <span className="text-xs text-gray-700 ml-1">
                  {item.recipient_phone}
                </span>
              </div>
            )}

            {/* Reference */}
            {item.reference && (
              <div className="flex items-center">
                <span className="text-xs text-gray-500 font-medium">Ref:</span>
                <span className="text-xs text-gray-700 ml-1 font-mono">
                  {item.reference}
                </span>
              </div>
            )}

            {/* Status and Date Row */}
            <div className="flex items-center justify-between pt-1">
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  item.status,
                )}`}
              >
                {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
              </span>
              <span className="text-xs text-gray-400">
                {formatDate(item.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const FilterModal = () =>
    showFilters && (
      <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black bg-opacity-50">
        <div className="w-full max-w-md bg-white rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Filters</h2>
            <button
              onClick={() => setShowFilters(false)}
              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
            >
              ×
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wallet
              </label>
              <select
                value={filters.wallet_type}
                onChange={(e) =>
                  setFilters({ ...filters, wallet_type: e.target.value })
                }
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                <option value="">All Wallets</option>
                <option value="affordable_data">Affordable Data</option>
                <option value="airtime_data">Airtime & Data</option>
              </select>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setFilters({
                    status: "",
                    wallet_type: "",
                    direction: "",
                    date_from: "",
                    date_to: "",
                  });
                  setShowFilters(false);
                }}
                className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-gray-700 font-medium"
              >
                Clear
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="flex-1 py-3 px-4 bg-yellow-400 rounded-xl text-black font-medium"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="px-4 py-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
            Transaction History
          </h1>
          <p className="text-gray-600 text-sm">Track all your transactions</p>
        </div>

        {/* Tabs */}
        <div className="px-4 pb-4 ">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="px-4 pb-4">
          <div className="flex space-x-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            <button
              onClick={() => setShowFilters(true)}
              className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center"
            >
              <SlidersHorizontal className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {loading && filteredData.length === 0 ? (
          <div>
            {[...Array(8)].map((_, i) => (
              <SkeletonLoader key={i} />
            ))}
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No transactions
            </h3>
            <p className="text-gray-500 text-sm">
              Your history will appear here
            </p>
          </div>
        ) : (
          <>
            {filteredData.map((item, index) => (
              <TransactionItem key={item.reference || index} item={item} />
            ))}

            {/* Load more trigger */}
            <div ref={observerTarget} className="py-4">
              {loadingMore && (
                <div>
                  {[...Array(3)].map((_, i) => (
                    <SkeletonLoader key={`loading-${i}`} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <FilterModal />
    </div>
  );
};

export default HistoryPage;
